/**
 * Footprint-aware token movement over a {@link Space} — the shared validation
 * the `move_token` intent enforces server-side and the VTT mirrors as a movement
 * overlay. **Generic over the grid** (takes a `Space<P>`), so the identical rule
 * runs on hex and square alike, and the same code answers both "where can this
 * token go?" (the overlay) and "is this destination legal?" (the reducer).
 *
 * A token occupies a multi-cell **Size footprint** (tick-tock growth on hex), so
 * the unit of legality is not a single cell but the whole footprint: a candidate
 * anchor is legal only if *every* cell its footprint covers is in bounds and
 * unoccupied by another token. The reducer and the overlay call these exact same
 * functions, so the affordance can never drift from the wall.
 *
 * Render-free and rules-free beyond geometry: terrain enters through the **same
 * seam** occupancy does. A solid cell — terrain-solid OR occupied by another token — is one shared
 * `blocked` set the caller composes; difficult terrain rides an optional
 * `moveCost(cell)` closure into the per-step `enterCost`. There is deliberately
 * **no second blocking system**: movement and collision both flow through this
 * one `blocked`/`inBounds`/`moveCost` context.
 */

import type { Space } from "./space";
import { type EnterCost, type Reachable, aStar, reachable } from "./path";

/** A placed token: just an id, its anchor cell, and its Lancer Size. */
export type Placement<P> = {
  id: string;
  anchor: P;
  size: number;
}

/**
 * The set of cell keys occupied by every placement except `exceptId` — the
 * occupancy index a move validates against (the moving token never collides with
 * its own current footprint).
 */
export function occupancyKeys<P>(
  space: Space<P>,
  placements: Iterable<Placement<P>>,
  exceptId?: string,
): Set<string> {
  const keys = new Set<string>();
  for (const p of placements) {
    if (p.id === exceptId) continue;
    for (const c of space.footprint(p.anchor, p.size).cells()) keys.add(space.key(c));
  }
  return keys;
}

/**
 * Whether a Size-`size` footprint anchored at `anchor` is **legal**: every cell
 * it covers is in bounds (`inBounds`) and not in the `blocked` occupancy set.
 */
/** Named arguments for {@link footprintFits}. */
export type FootprintFitsArgs<P> = {
  space: Space<P>;
  anchor: P;
  size: number;
  blocked: Set<string>;
  inBounds: (p: P) => boolean;
}

export function footprintFits<P>(args: FootprintFitsArgs<P>): boolean {
  const { space, anchor, size, blocked, inBounds } = args;
  return space
    .footprint(anchor, size)
    .cells()
    .every((c) => inBounds(c) && !blocked.has(space.key(c)));
}

/** Named arguments for {@link footprintEndpointFits}. */
export type FootprintEndpointFitsArgs<P> = {
  space: Space<P>;
  anchor: P;
  size: number;
  blocked: Set<string>;
  /** Occupied-but-passable cell keys (see {@link MovementContext.noStop}). */
  noStop?: Set<string>;
  inBounds: (p: P) => boolean;
}

/**
 * Whether a Size-`size` footprint anchored at `anchor` may legally **end** a
 * movement there (RAW p.62 OBSTRUCTION: "they can't end a movement in a space
 * that is occupied by another character or object") — every cell it covers must
 * be in bounds and free of BOTH `blocked` (solid — terrain, or an occupant that
 * also obstructs travel) and `noStop` (occupied-but-passable — an ally or a
 * smaller hostile the mover may travel *through* but not stop *on*).
 *
 * This is {@link footprintFits} widened by one more forbidden set: every legal
 * endpoint is a legal mid-path cell, but not vice versa. This is the **one**
 * named seam for "may a footprint stop here" — `reachableAnchors`'s post-filter
 * and `moveCostTo`/`canMoveTo`'s dest gate both resolve through it (indirectly,
 * via that same filter), and it's exported so the reducer's placement/
 * `place_token` overlap check and, later, teleport's "destination must be free"
 * test reuse it rather than re-deriving `blocked ∪ noStop` inline.
 */
export function footprintEndpointFits<P>(args: FootprintEndpointFitsArgs<P>): boolean {
  const { space, anchor, size, blocked, noStop, inBounds } = args;
  return space
    .footprint(anchor, size)
    .cells()
    .every((c) => {
      if (!inBounds(c)) return false;
      const key = space.key(c);
      return !blocked.has(key) && !(noStop?.has(key) ?? false);
    });
}

/** Inputs shared by the reach/legality helpers — the live map + occupancy state. */
export type MovementContext<P> = {
  /**
   * Cell keys that are **solid** — impassable to a footprint. The *one* shared
   * occupancy predicate: terrain-solid OR occupied by another token,
   * composed by the caller (build via {@link occupancyKeys}
   * for tokens, union the terrain-solid keys in). Movement and collision share
   * this set; there is no separate terrain-blocking path.
   */
  blocked: Set<string>;
  /**
   * Cell keys that are **occupied but passable** — traversable mid-path, but a
   * footprint may not *end* a movement overlapping one (RAW p.62: allies never
   * obstruct, and a hostile smaller than the mover doesn't either, but "can't end
   * a movement in a space that is occupied by another character or object" still
   * applies to both). Optional; defaults to empty. The flood ({@link reachableAnchors}) still *travels* through these cells
   * (this set never enters {@link footprintEnterCost}'s `fits` gate); only the
   * returned anchors and the dest checks ({@link moveCostTo}/{@link canMoveTo})
   * filter through {@link footprintEndpointFits}. Classification (who lands in
   * `blocked` vs. `noStop`) is the domain layer's job — this package only knows
   * "solid" vs. "occupied-but-passable", not why.
   */
  noStop?: Set<string>;
  /** Whether a cell is on the map (the otherwise-infinite grid's bound). */
  inBounds: (p: P) => boolean;
  /**
   * Cost to *enter* a cell, before footprint/bounds checks (difficult terrain).
   * Defaults to 1 per step. A blocked/off-map cell is impassable regardless of
   * what this returns; this only scales the budget cost of an otherwise-legal
   * cell. Must be `>= 1` so the Dijkstra/A-star heuristic stays admissible.
   */
  moveCost?: (p: P) => number;
  /**
   * Whether a thin wall on the edge between two adjacent cells blocks movement
   * across it. Unlike `blocked` (a solid *cell*), an edge wall sits *between*
   * `from` and `to`: `blocksEdge(to, from)` true ⇒ you can't step from `from`
   * into `to`, even though both cells are otherwise passable. It rides
   * `enterCost(to, from)`'s existing two-arg seam. Order-independent by the canonicalizer the encounter layer supplies; defaults
   * to "no edge blocks". Note: a footprint occupies multiple cells, but movement
   * steps the *anchor* one cell at a time, so this gates the anchor's step edge.
   */
  blocksEdge?: (to: P, from: P) => boolean;
  /**
   * Whether the edge `from → to` **stops** movement: entering `to` across it is
   * legal — `to` joins {@link reachableAnchors}'s map as an ordinary,
   * endpoint-legal anchor, and {@link anchorPathTo}/{@link canMoveTo}/
   * {@link moveCostTo} accept it as a destination exactly as if the field were
   * absent — but `to` is never *expanded*: nothing beyond it is reached BY WAY
   * OF it (a destination beyond a stopping edge reads as unreachable, the same
   * as if it were simply out of budget). A pure function of the two anchors —
   * this package has no notion of hostiles, Size, or engagement, only that some
   * edges are one-way stops; the caller's closure decides what "stopping" means
   * (Lancer's engagement mid-move stop is the motivating case, wired at the
   * domain layer). This is a **third**, orthogonal component of the one
   * movement seam alongside `blocked` (solid — never enterable) and `noStop`
   * (occupied-but-passable — enterable, but never endpoint-legal): `stopsAt`'s
   * anchor is both enterable AND endpoint-legal, it's simply never a
   * thoroughfare. Optional; absent ⇒ no edge stops. Rides `path.ts`'s generic
   * `PathOptions.stopsAt`, leaving the `blocked`/`noStop`/`blocksEdge` gates
   * above untouched.
   */
  stopsAt?: (from: P, to: P) => boolean;
  /**
   * An extra endpoint rule beyond occupancy: `false` refuses `anchor` as a destination while leaving
   * it traversable, the same way a `noStop` cell is. For a rule that depends on where the mover
   * ends, not on which cells it covers (the domain's grapple push: a mover may end over its
   * dragged partner only when the partner has a legal cell to be pushed to). Read by
   * {@link reachableAnchors} and {@link movePathCost}, the two endpoint checks. Optional; absent ⇒
   * no extra rule.
   */
  endpointOk?: (anchor: P) => boolean;
}

/**
 * Legal destination anchors for a token of Size `size` starting at `from`, within
 * a movement `budget` (Lancer Speed). Every cell its anchor can step to where the
 * **whole footprint** fits (in bounds, no overlap) — a footprint-aware wrapper
 * over the generic {@link reachable}, keyed by `space.key`, each with its cheapest
 * arrival cost. `from` itself is always included (cost 0).
 */
/** Named arguments for {@link reachableAnchors}. */
export type ReachableAnchorsArgs<P> = {
  space: Space<P>;
  from: P;
  size: number;
  budget: number;
  ctx: MovementContext<P>;
}

/**
 * The per-step cost a footprint-aware move charges — the *one* `enterCost` the
 * reach flood ({@link reachableAnchors}), the legality check ({@link canMoveTo}),
 * and the path preview ({@link anchorPathTo}) all share, so none can drift from the
 * others. Footprint fit (solid/off-map) is the impassability gate; an edge wall on
 * the `from → to` boundary is equally impassable; difficult terrain scales the
 * cost of an otherwise-legal step
 * (default 1). All ride this one closure — no parallel pass.
 */
function footprintEnterCost<P>(space: Space<P>, size: number, ctx: MovementContext<P>): EnterCost<P> {
  const moveCost = ctx.moveCost;
  const blocksEdge = ctx.blocksEdge;
  const fits = (anchor: P) => footprintFits({ space, anchor, size, blocked: ctx.blocked, inBounds: ctx.inBounds });
  return (to, from) => {
    if (!fits(to)) return Infinity;
    // Body no-corner-squeeze (square diagonals only): a diagonal step routes the whole
    // footprint through the corner between its two orthogonal flanks, so it's legal only if
    // at least one flank can be *rounded* — that flank, as the body's anchor, fits AND
    // neither leg of the detour through it is walled. If no flank is roundable the body
    // can't thread the gap: it can round a single open corner, but not squeeze between two
    // diagonally-placed blockers, cut a straight wall on the diagonal, or slip diagonally
    // out of a walled-in corner. `diagonalFlanks` is `[]` for orthogonal/hex steps, so this
    // is a no-op off the square diagonal.
    const flanks = space.diagonalFlanks(from, to);
    if (flanks.length > 0) {
      const roundable = (f: P) => fits(f) && !(blocksEdge?.(f, from) || blocksEdge?.(to, f));
      if (!flanks.some(roundable)) return Infinity;
    }
    // Edge walls, per footprint cell. The footprint translates one step, so EVERY cell it
    // covers crosses an edge — gating only the anchor's edge (above) lets a Size > 1 body
    // slip the rest of itself through a wall on its leading face. The footprint enumerates
    // in a translation-stable order, so `from`-cell i and `to`-cell i are the same offset:
    // their connecting edge is exactly what that cell crosses. Each crossing must clear a
    // direct wall and, on a square diagonal, the wall corner (blocked iff BOTH flank legs
    // are walled). Walls only here — the destination's solidity is already `fits(to)`, the
    // body squeeze is the anchor rule above. Skipped entirely when there are no walls.
    if (blocksEdge) {
      const fromCells = space.footprint(from, size).cells();
      const toCells = space.footprint(to, size).cells();
      for (let i = 0; i < fromCells.length; i++) {
        const cFrom = fromCells[i]!;
        const cTo = toCells[i]!;
        if (blocksEdge(cTo, cFrom)) return Infinity;
        const cellFlanks = space.diagonalFlanks(cFrom, cTo);
        if (cellFlanks.length > 0 && cellFlanks.every((f) => blocksEdge(f, cFrom) || blocksEdge(cTo, f))) {
          return Infinity;
        }
      }
    }
    return moveCost ? moveCost(to) : 1;
  };
}

export function reachableAnchors<P>(args: ReachableAnchorsArgs<P>): Map<string, Reachable<P>> {
  const { space, from, size, budget, ctx } = args;
  const flood = reachable({
    space,
    start: from,
    budget,
    enterCost: footprintEnterCost(space, size, ctx),
    stopsAt: ctx.stopsAt,
  });
  // Endpoint enforcement lives HERE, at the wrapper layer, not in `fits`/
  // `footprintEnterCost` — the flood above already traversed every `noStop` cell
  // freely (it's absent from that gate); this filters the *returned* map so a
  // stop-illegal anchor never surfaces as a legal destination, without touching
  // which cells were reachable as intermediates. Skipped entirely when `noStop`
  // is absent/empty.
  const noStop = ctx.noStop;
  const endpointOk = ctx.endpointOk;
  if ((!noStop || noStop.size === 0) && !endpointOk) return flood;
  const out = new Map<string, Reachable<P>>();
  for (const [key, arrival] of flood) {
    // The start anchor stays: not moving is always legal, whatever an endpoint rule says.
    if (space.key(arrival.cell) !== space.key(from) && endpointOk && !endpointOk(arrival.cell)) continue;
    if (footprintEndpointFits({ space, anchor: arrival.cell, size, blocked: ctx.blocked, noStop, inBounds: ctx.inBounds })) {
      out.set(key, arrival);
    }
  }
  return out;
}

/** Named arguments for {@link anchorPathTo}. */
export type AnchorPathArgs<P> = {
  space: Space<P>;
  from: P;
  dest: P;
  size: number;
  budget: number;
  ctx: MovementContext<P>;
}

/**
 * Prefer orthogonal steps over equal-cost diagonals (square diagonals cost the same as a
 * straight step, so an unbiased A* zig-zags arbitrarily). A diagonal step is exactly the one
 * with flank cells (`diagonalFlanks` is `[]` for orthogonal/hex steps), so this penalizes only
 * the squared-grid diagonal — a pure cosmetic tiebreak, never a change to cost or reach.
 */
const orthogonalFirst =
  <P>(space: Space<P>) =>
  (to: P, from: P): number =>
    space.diagonalFlanks(from, to).length > 0 ? 1 : 0;

/**
 * The cheapest legal footprint path from `from` to `dest` (anchors, inclusive of
 * both ends), or `null` when `dest` is **not reachable within `budget`** — the
 * read side the VTT paints as a move-path preview while a token is dragged. Gated
 * by the *same* {@link canMoveTo} the `move_token` reducer enforces, so the preview
 * only ever appears for a drop the move would actually accept; the trail itself is
 * A* over the *same* {@link footprintEnterCost} the reach flood uses, so it threads
 * the identical fit/edge/difficult-terrain rules. `from === dest` yields `[from]`.
 */
export function anchorPathTo<P>(args: AnchorPathArgs<P>): P[] | null {
  const { space, from, dest, size, budget, ctx } = args;
  if (!canMoveTo({ space, from, dest, size, budget, ctx })) return null;
  return aStar({
    space,
    start: from,
    goal: dest,
    enterCost: footprintEnterCost(space, size, ctx),
    tieBreak: orthogonalFirst(space),
    stopsAt: ctx.stopsAt,
  });
}

/**
 * The cheapest cost to move a Size-`size` token from `from` to `dest` within
 * `budget` — the cheapest arrival cost of `dest` in the reach flood — or `null`
 * when `dest` is **not reachable within `budget`** (off-map, overlapping, walled,
 * a footprint that would end overlapping a `noStop` occupant, beyond a
 * `stopsAt` stopping edge (though the stop anchor itself stays reachable), or
 * simply too far — see {@link reachableAnchors}, which this reads and which
 * already excludes stop-illegal anchors from the map, so no separate `noStop` check is needed
 * here). `dest === from` is cost 0 (a no-op move). This is what a
 * budget-consuming `move_token` reads: the cost it charges the move budget, and a
 * `null` is the hard-reject. {@link canMoveTo} is the boolean shadow of this.
 */
export function moveCostTo<P>(args: CanMoveToArgs<P>): number | null {
  const { space, from, dest, size, budget, ctx } = args;
  if (space.key(from) === space.key(dest)) return 0;
  const arrival = reachableAnchors({ space, from, size, budget, ctx }).get(space.key(dest));
  return arrival ? arrival.cost : null;
}

/**
 * Whether a token of Size `size` may legally move from `from` to `dest` within
 * `budget`. `dest === from` is always legal (a no-op move). The single predicate
 * `move_token` checks before mutating: the destination footprint must fit *and*
 * be reachable within budget through fitting intermediate cells. The boolean
 * shadow of {@link moveCostTo} (reachable ⇔ a finite cost), kept as its own name
 * for the call sites that only need the yes/no (the overlay, the path preview).
 */
/** Named arguments for {@link canMoveTo} / {@link moveCostTo}. */
export type CanMoveToArgs<P> = {
  space: Space<P>;
  from: P;
  dest: P;
  size: number;
  budget: number;
  ctx: MovementContext<P>;
}

export function canMoveTo<P>(args: CanMoveToArgs<P>): boolean {
  return moveCostTo(args) !== null;
}

/** Named arguments for {@link movePathCost}. */
export type MovePathCostArgs<P> = {
  space: Space<P>;
  /** The anchor path, `from` first and the destination last. */
  path: readonly P[];
  size: number;
  ctx: MovementContext<P>;
}

/** Why {@link movePathCost} refused a path, and the anchor it refused at. */
export type MovePathFault<P> = {
  cause: "not_adjacent" | "impassable" | "stopped_early" | "endpoint_occupied";
  at: P;
}

/**
 * The cost of walking a caller-chosen anchor `path`, step by step, under the same
 * {@link footprintEnterCost} the reach flood and {@link anchorPathTo} charge — or the first
 * fault: a step to a non-neighbour, a step the footprint can't take, a stopping edge
 * (`ctx.stopsAt`) anywhere but the last step, or a destination footprint that may not end a
 * movement ({@link footprintEndpointFits}). Mid-path `noStop` cells are passable, as in the
 * flood. `stopped` says the LAST step crossed a stopping edge. Budget is the caller's check.
 */
export function movePathCost<P>(args: MovePathCostArgs<P>): { ok: true; cost: number; stopped: boolean } | ({ ok: false } & MovePathFault<P>) {
  const { space, path, size, ctx } = args;
  const enter = footprintEnterCost(space, size, ctx);
  let cost = 0;
  let stopped = false;
  for (let i = 1; i < path.length; i++) {
    const from = path[i - 1]!;
    const to = path[i]!;
    if (!space.neighbors(from).some((n) => space.key(n) === space.key(to))) return { ok: false, cause: "not_adjacent", at: to };
    const step = enter(to, from);
    if (!Number.isFinite(step)) return { ok: false, cause: "impassable", at: to };
    stopped = ctx.stopsAt?.(from, to) ?? false;
    if (stopped && i < path.length - 1) return { ok: false, cause: "stopped_early", at: to };
    cost += step;
  }
  const end = path[path.length - 1]!;
  if (path.length >= 2 && (!footprintEndpointFits({ space, anchor: end, size, blocked: ctx.blocked, noStop: ctx.noStop, inBounds: ctx.inBounds }) || ctx.endpointOk?.(end) === false)) {
    return { ok: false, cause: "endpoint_occupied", at: end };
  }
  return { ok: true, cost, stopped };
}

/** Named arguments for {@link anchorLegPath}. */
export type AnchorLegPathArgs<P> = {
  space: Space<P>;
  from: P;
  dest: P;
  size: number;
  ctx: MovementContext<P>;
}

/**
 * The cheapest footprint path from `from` to `dest` with no budget and no endpoint rule: `dest`
 * may be an occupied-but-passable cell, because a leg of a waypointed route passes through it
 * rather than stopping. The same step cost and orthogonal tiebreak as {@link anchorPathTo}.
 * `null` when no path exists.
 */
export function anchorLegPath<P>(args: AnchorLegPathArgs<P>): P[] | null {
  const { space, from, dest, size, ctx } = args;
  return aStar({
    space,
    start: from,
    goal: dest,
    enterCost: footprintEnterCost(space, size, ctx),
    tieBreak: orthogonalFirst(space),
    stopsAt: ctx.stopsAt,
  });
}
